import { Component } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { AlertTriangle, RefreshCw, Copy, ChevronDown, ChevronUp } from 'lucide-react';
import { toast } from 'sonner';

class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { 
      hasError: false, 
      error: null, 
      errorInfo: null,
      showDetails: false 
    };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    this.setState({ 
      error, 
      errorInfo 
    });
    
    // Log error to console for debugging
    console.error('ErrorBoundary caught an error:', error, errorInfo);
    
    // You could also send this to an error reporting service
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
  };

  handleCopyError = () => {
    const errorText = `
Error: ${this.state.error?.toString()}

Component Stack:
${this.state.errorInfo?.componentStack || 'N/A'}

Page: ${window.location.href}
Time: ${new Date().toISOString()}
    `.trim();
    
    navigator.clipboard.writeText(errorText);
    toast.success('Error details copied to clipboard');
  };

  render() {
    if (this.state.hasError) {
      const { fallback, pageName = 'page' } = this.props;
      
      // Allow custom fallback UI
      if (fallback) {
        return fallback({
          error: this.state.error,
          errorInfo: this.state.errorInfo,
          retry: this.handleRetry
        });
      }

      return (
        <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
          <Card className="max-w-2xl w-full shadow-lg">
            <CardHeader className="bg-red-50 border-b border-red-100">
              <CardTitle className="flex items-center gap-2 text-red-700">
                <AlertTriangle className="h-5 w-5" />
                Something went wrong
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6 space-y-4">
              <p className="text-slate-600">
                An error occurred while loading the <strong>{pageName}</strong>. 
                This has been logged for debugging.
              </p>
              
              {/* Error Summary */}
              <div className="bg-slate-100 p-3 rounded-md">
                <p className="font-mono text-sm text-red-600 break-all">
                  {this.state.error?.toString() || 'Unknown error'}
                </p>
              </div>

              {/* Expandable Details */}
              <button
                onClick={() => this.setState(s => ({ showDetails: !s.showDetails }))}
                className="flex items-center gap-2 text-sm text-slate-500 hover:text-slate-700"
              >
                {this.state.showDetails ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                {this.state.showDetails ? 'Hide' : 'Show'} technical details
              </button>
              
              {this.state.showDetails && (
                <div className="bg-slate-900 text-slate-100 p-4 rounded-md overflow-auto max-h-64">
                  <pre className="text-xs whitespace-pre-wrap">
                    {this.state.errorInfo?.componentStack || 'No stack trace available'}
                  </pre>
                </div>
              )}

              {/* Actions */}
              <div className="flex gap-3 pt-4 border-t">
                <Button onClick={this.handleRetry} className="gap-2">
                  <RefreshCw className="h-4 w-4" />
                  Try Again
                </Button>
                <Button variant="outline" onClick={this.handleCopyError} className="gap-2">
                  <Copy className="h-4 w-4" />
                  Copy Error Details
                </Button>
                <Button 
                  variant="outline" 
                  onClick={() => window.location.href = '/dashboard'}
                >
                  Go to Dashboard
                </Button>
              </div>

              <p className="text-xs text-slate-400 pt-2">
                If this problem persists, please contact support with the error details above.
              </p>
            </CardContent>
          </Card>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
